import type { RequireAtLeastOne } from 'type-fest';

import { Statuses } from './api';
import { Dialog } from './dialog';
import { Entity } from './entity';
import { Insight } from './insights';
import { Intent } from './intent';
import { Webhook } from './webhook';

export enum ChannelType {
  CHAT = 'chat',
  EMAIL = 'email',
  VOICE = 'voice',
}

export type ChannelGuidelines = Partial<Record<ChannelType, string | null>>;

export type Brains = { brains: Brain[] };

export type BrainMetadata = {
  onboarding_status?: string;
  onboarding_type?: string;
  insights_enabled?: boolean;
  improvements_enabled?: boolean;
};

export type CustomGuideline = { title: string; subtitle: string; body: string; created_at: number };

export type BrainReminder = {
  reminder_id: string;
  reminder_seconds: number;
  node_id: string;
  /** @deprecated */
  minutes?: number;
};

export type BrainGuidelines = Partial<{
  name: string;
  company_info: string;
  response_size: 'short' | 'medium' | 'long';
  humor_level: number;
  formality_level: number;
  respectfulness_level: number;
  creativity_level: number;
  tone_of_voice: number;
  factuality_sensitivity?: FactualitySensitivity;

  /**
   * True if the customer has a handover mechanism
   */
  support_handover: boolean;

  /**
   * Up to 3 reminders with the number of seconds of inactivity
   * before the reminder is sent
   */
  reminders: BrainReminder[];

  // Guidelines
  goal: string;
  overview: string;
  features: string;
  loyalty: string;
  objections: { message: string; response: string }[] | string;
  custom_instructions: string;
  live_instructions: string;
  custom_guidelines?: CustomGuideline[];
  channel_guidelines?: ChannelGuidelines;
  match_user_language?: boolean;

  // Tracking metadata (system-managed, not user-provided)
  updated_at: number;
  updated_by: string | null;
}>;

export enum BrainLanguage {
  ENGLISH = 'en',
  SPANISH = 'es',
  PORTUGUESE = 'pt',
  PORTUGUESE_BRAZILIAN = 'pt-br',
  GREEK = 'el',
  ROMANIAN = 'ro',
  ITALIAN = 'it',
  FRENCH = 'fr',
  DANISH = 'da',
  GERMAN = 'de',
  BULGARIAN = 'bg',
  CZECH = 'cs',
  POLISH = 'pl',
  INDONESIAN = 'id',
  DUTCH = 'nl',
  DUTCH_BELGIAN = 'nl-be',
  SERBIAN = 'sr',
  CROATIAN = 'hr',
  SWEDISH = 'sv',
  RUSSIAN = 'ru',
  ALBANIAN = 'sq',
  TAGALOG = 'tl',
  WELSH = 'cy',
  ARABIC = 'ar',
  JAPANESE = 'ja',
  TURKISH = 'tr',
  CHINESE = 'zh',
  VIETNAMESE = 'vi',
  THAI = 'th',
  MALAY = 'ms',
}

export enum BrainType {
  IMPORT = 'import',
  SUPPORT = 'support',

  /** @deprecated */
  SALES_ECOMMERCE = 'sales_ecommerce',
  /** @deprecated */
  SALES_FINTECH = 'sales_fintech',
  EARLY_ENGAGEMENT = 'engagement',
  ACTIVATION = 'activation',
  UPSELL = 'upsell',
  DEBT_COLLECTION = 'debt_collection',
}

export enum Tool {
  TRIGGER_WORKFLOW = 'trigger_workflow',
  COMPOSE_RICH_RESPONSE = 'compose_rich_response',
  EXECUTE_CODE = 'execute_code',
  VOICEMAIL_DETECTION = 'voicemail_detection',
  SEARCH_KNOWLEDGE_BASE = 'search_knowledge_base',
  ASK_QUESTIONS = 'ask_questions',
}

export enum SearchStrategyMode {
  PREFETCH = 'prefetch',
  ON_DEMAND = 'on_demand',
}

export type SearchStrategy = { mode: SearchStrategyMode; max_results: number | null };

export enum FactualitySensitivity {
  NONE = 'none',
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export type Brain = {
  readonly account_id: string;
  readonly brain_id: string;
  readonly brain_parent_id: string;
  readonly created_by: string;
  readonly active_sessions?: number;

  collection_id?: string;
  confidence_threshold: number;
  created: string;
  description: string;
  dialog_count: number;
  dialogs?: Dialog[];
  brain_type: BrainType;
  entity_count: number;
  entities?: Entity[];
  greeklish: boolean;
  inactivity_timeout: number;
  intent_count: number;
  intents?: Intent[];
  language: BrainLanguage;
  /** Languages the agent also replies in, `["*"]` for every language. Never holds `language`. */
  supported_languages?: string[];
  language_model_id?: string;
  /**
   * UUID of the dialog node a conversation always begins at (the "starting node").
   * Empty/undefined = no starting node / default behavior.
   */
  starting_node_id?: string | null;
  last_trained: string;
  last_used_at?: string;
  metadata: BrainMetadata;
  guidelines: BrainGuidelines;
  name: string;
  search_strategy?: SearchStrategy;
  status: Statuses;
  updated: string;
  version_count: number;
  version: number;
  versions: number;
  tools?: Tool[];
  webhooks?: Webhook[];
  insights?: Insight[];
};

export type AIAgent = Brain;

export type BrainVersion = Omit<Brain, 'metadata' | 'name' | 'versions' | 'account_id'>;

export type BrainFromLogs = {
  name: string;
  language: string;
  type: string;
  integration_id?: string;
  logs_file?: string;
};

export type PartialBrain = RequireAtLeastOne<Partial<Brain>, 'brain_id'>;
